import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { X } from 'lucide-react';

/**
 * Toasts with an optional action (spec 5.13: every save shows a toast with
 * Undo for 10 seconds). Rendered bottom-center; keyboard users can reach
 * the action button.
 */
export interface ToastInput {
  message: string;
  tone?: 'neutral' | 'ok' | 'warn' | 'danger';
  action?: { label: string; onClick: () => void | Promise<void> };
  /** ms; defaults to 10 s when there is an action, 4 s otherwise */
  duration?: number;
}

interface Toast extends ToastInput {
  id: number;
}

interface ToastApi {
  show(t: ToastInput): void;
}

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, number>());

  const dismiss = useCallback((id: number) => {
    setToasts((ts) => ts.filter((t) => t.id !== id));
    const timer = timers.current.get(id);
    if (timer) window.clearTimeout(timer);
    timers.current.delete(id);
  }, []);

  const show = useCallback(
    (t: ToastInput) => {
      const id = nextId.current++;
      setToasts((ts) => [...ts.slice(-3), { ...t, id }]);
      const duration = t.duration ?? (t.action ? 10_000 : 4_000);
      timers.current.set(id, window.setTimeout(() => dismiss(id), duration));
    },
    [dismiss]
  );

  const api = useMemo(() => ({ show }), [show]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      {/* z-100: above modals, so an Undo toast raised from inside one is readable (see ui/dialog.tsx). */}
      <div className="pointer-events-none fixed inset-x-0 bottom-4 z-[100] flex flex-col items-center gap-2 px-4" aria-live="polite">
        {toasts.map((t) => (
          <div
            key={t.id}
            role="status"
            className={`pointer-events-auto flex items-center gap-3 rounded-md px-4 py-2 text-sm shadow-lg ${
              t.tone === 'danger'
                ? 'bg-danger text-white'
                : t.tone === 'warn'
                  ? 'bg-warn text-white'
                  : t.tone === 'ok'
                    ? 'bg-ok text-white'
                    : 'bg-ink text-white'
            }`}
          >
            <span>{t.message}</span>
            {t.action && (
              <button
                type="button"
                className="rounded border border-white/40 px-2 py-0.5 font-semibold hover:bg-white/15"
                onClick={() => {
                  void t.action?.onClick();
                  dismiss(t.id);
                }}
              >
                {t.action.label}
              </button>
            )}
            <button type="button" className="rounded p-0.5 opacity-70 hover:opacity-100" onClick={() => dismiss(t.id)} aria-label="Dismiss">
              <X size={14} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used inside ToastProvider');
  return ctx;
}
